import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface TabNavItem {
  hidden?: boolean
  index?: number
  label: string
  to: string
  value?: string
}

interface TabNavContextValue {
  primaryItems: TabNavItem[]
  registerPrimaryItems: (items: TabNavItem[]) => void
  registerSecondaryItems: (items: TabNavItem[]) => void
  secondaryItems: TabNavItem[]
}

const TabNavContext = createContext<TabNavContextValue | null>(null)

TabNavContext.displayName = 'TabNavContext'

interface TabNavContextProviderProps {
  children: React.ReactNode
}

export const TabNavContextProvider: React.FC<TabNavContextProviderProps> = ({ children }) => {
  const [primaryItems, setPrimaryItems] = useState<TabNavItem[]>([])
  const [secondaryItems, setSecondaryItems] = useState<TabNavItem[]>([])

  const registerPrimaryItems = useCallback((items: TabNavItem[]): void => {
    setPrimaryItems(items)
  }, [])

  const registerSecondaryItems = useCallback((items: TabNavItem[]): void => {
    setSecondaryItems(items)
  }, [])

  const value = useMemo(
    () => ({
      primaryItems,
      registerPrimaryItems,
      registerSecondaryItems,
      secondaryItems,
    }),
    [primaryItems, registerPrimaryItems, registerSecondaryItems, secondaryItems]
  )

  return <TabNavContext.Provider value={value}>{children}</TabNavContext.Provider>
}

export const useTabNavContext = (): TabNavContextValue => {
  const value = useContext(TabNavContext)
  if (!value) throw new Error('TabNav context must be initialized before using it')
  return value
}

export const getVisibleTabNavItems = (items: TabNavItem[]): TabNavItem[] => items.filter((item) => !item.hidden)

export const getActiveTabNavItem = (items: TabNavItem[], pathname: string): TabNavItem | undefined =>
  getVisibleTabNavItems(items)
    .sort((firstItem, secondItem) => secondItem.to.length - firstItem.to.length)
    .find((item) => pathname.startsWith(item.to))
